// Hub Ops panel — drives the operational micro-hub transfer chain for
// Micro Hub Leads + Main Hub Managers:
//   • Action Center: status-aware next action for parcels sitting at my hub
//     (Generate Transfer OTP / Dispatch Line-haul / Receive Line-haul / Out for Delivery)
//   • Verify Transfer OTP: parcels where I'm the receiving hub + manual entry
import { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Loader2, RefreshCw, ArrowRightLeft, Truck, PackageCheck, KeyRound, Send, Copy, ShieldCheck,
} from "lucide-react";

const ORANGE = "#FF6B00";

// status → primary outbound action for the hub currently holding the parcel
const ACTIONS = {
  received_at_origin_micro_hub: { label: "Generate Transfer OTP → Main Hub", kind: "generate", icon: ArrowRightLeft },
  received_at_origin_main_hub: { label: "Hand over for Line-haul", kind: "dispatch", icon: Truck },
  in_linehaul: { label: "Receive from Courier", kind: "receive", icon: PackageCheck },
  received_at_dest_main_hub: { label: "Generate Transfer OTP → Micro Hub", kind: "generate", icon: ArrowRightLeft },
  received_at_dest_micro_hub: { label: "Send Out for Delivery", kind: "ofd", icon: Send },
};

export default function HubOpsPanel() {
  const [queue, setQueue] = useState({ incoming: [], to_verify: [], linehaul_inbound: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [otpResult, setOtpResult] = useState(null); // {tracking_id, otp, to_hub_name}
  const [vtid, setVtid] = useState("");
  const [votp, setVotp] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub-ops/queue");
      setQueue(data);
    } catch {
      /* non-hub role → empty */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (order) => {
    const cfg = ACTIONS[order.status];
    if (!cfg) return;
    setBusy(order.tracking_id);
    try {
      if (cfg.kind === "generate") {
        const { data } = await api.post(`/hub-ops/${order.tracking_id}/transfer/generate-otp`);
        setOtpResult({ tracking_id: order.tracking_id, otp: data.transfer_otp, to_hub_name: data.to_hub_name });
        toast.success("Transfer OTP generated — share with the receiving hub");
      } else if (cfg.kind === "dispatch") {
        const { data } = await api.post(`/hub-ops/${order.tracking_id}/dispatch-linehaul`);
        toast.success(data.linehaul_awb
          ? `Handed to line-haul courier · AWB ${data.linehaul_awb}`
          : `${order.tracking_id} handed over for line-haul`);
      } else if (cfg.kind === "receive") {
        await api.post(`/hub-ops/${order.tracking_id}/receive-linehaul`);
        toast.success(`${order.tracking_id} received from courier`);
      } else if (cfg.kind === "ofd") {
        await api.post(`/hub-ops/${order.tracking_id}/out-for-delivery`);
        toast.success(`${order.tracking_id} out for delivery`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  const verify = async (tid, otp) => {
    if (!tid || !otp) { toast.error("Enter Tracking ID + OTP"); return; }
    setBusy(tid);
    try {
      const { data } = await api.post(`/hub-ops/${tid}/transfer/verify-otp`, { otp });
      toast.success(`${tid} → ${data.status}`);
      setVtid(""); setVotp("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy("");
    }
  };

  const actionItems = [...(queue.incoming || []), ...(queue.linehaul_inbound || [])]
    .filter((o, i, arr) => arr.findIndex((x) => x.tracking_id === o.tracking_id) === i)
    .filter((o) => ACTIONS[o.status]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-5" data-testid="hub-ops-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
          <ArrowRightLeft className="h-5 w-5" style={{ color: ORANGE }} /> Hub Transfers &amp; Dispatch
        </h3>
        <button onClick={load} className="text-slate-400 hover:text-[#FF6B00]" aria-label="Refresh" data-testid="hubops-refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Action center */}
      <div>
        <div className="text-xs font-semibold text-slate-500 mb-2">ACTION CENTER (parcels at my hub)</div>
        {loading ? (
          <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : actionItems.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-4" data-testid="hubops-action-empty">Nothing to dispatch right now.</div>
        ) : (
          <div className="space-y-2" data-testid="hubops-action-list">
            {actionItems.map((o) => {
              const cfg = ACTIONS[o.status];
              const Icon = cfg.icon;
              return (
                <div key={o.tracking_id} className="flex items-center justify-between border border-slate-100 rounded-xl px-3 py-2"
                     data-testid={`action-${o.tracking_id}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-semibold text-slate-800">{o.tracking_id}</div>
                    <div className="text-[11px] text-slate-500 truncate">{o.package_description || "Parcel"} · {o.status}</div>
                  </div>
                  <Button size="sm" onClick={() => act(o)} disabled={busy === o.tracking_id}
                          style={{ background: ORANGE }} className="text-white rounded-lg text-xs" data-testid={`action-btn-${o.tracking_id}`}>
                    {busy === o.tracking_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Icon className="h-3.5 w-3.5 mr-1" /> {cfg.label}</>}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Verify transfer OTP */}
      <div className="border-t border-slate-100 pt-4">
        <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
          <ShieldCheck className="h-4 w-4" /> VERIFY INCOMING TRANSFER OTP
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <Input placeholder="Tracking ID" value={vtid} onChange={(e) => setVtid(e.target.value.toUpperCase())}
                 className="rounded-xl font-mono" data-testid="hubops-vtid" />
          <Input placeholder="6-digit OTP" value={votp} onChange={(e) => setVotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 className="rounded-xl font-mono w-full sm:w-36" data-testid="hubops-votp" />
          <Button onClick={() => verify(vtid.trim(), votp.trim())} disabled={!!busy}
                  style={{ background: ORANGE }} className="text-white rounded-xl" data-testid="hubops-verify-btn">
            <KeyRound className="h-4 w-4 mr-1" /> Verify
          </Button>
        </div>
        {(queue.to_verify || []).length === 0 ? (
          <div className="text-center text-slate-400 text-xs py-2" data-testid="hubops-verify-empty">No incoming transfers awaiting your OTP.</div>
        ) : (
          <div className="space-y-2" data-testid="hubops-verify-list">
            {queue.to_verify.map((o) => (
              <div key={o.tracking_id} className="flex items-center justify-between border border-slate-100 rounded-xl px-3 py-2"
                   data-testid={`verify-${o.tracking_id}`}>
                <div className="font-mono text-sm font-semibold text-slate-800">{o.tracking_id}</div>
                <button onClick={() => { setVtid(o.tracking_id); document.querySelector('[data-testid="hubops-votp"]')?.focus(); }}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-[#C2410C] border border-orange-200 hover:bg-orange-100">
                  Enter OTP
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* OTP result modal */}
      {otpResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOtpResult(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()} data-testid="hubops-otp-modal">
            <ArrowRightLeft className="h-8 w-8 mx-auto" style={{ color: ORANGE }} />
            <h4 className="font-bold text-slate-800 mt-2">Transfer OTP</h4>
            <p className="text-xs text-slate-500">Share with <b>{otpResult.to_hub_name}</b> staff to confirm receipt.</p>
            <div className="text-4xl font-extrabold tracking-[0.3em] text-[#FF6B00] my-3" data-testid="hubops-otp-value">{otpResult.otp}</div>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(otpResult.otp); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
              <Button onClick={() => setOtpResult(null)} style={{ background: ORANGE }} className="text-white">Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
